import type { ServicePageData } from './types';

const parasols: ServicePageData = {
  slug: 'parasols',
  eyebrow: 'Signage',
  title: 'Up your brand exposure, not your',
  titleAccent: 'sun exposure.',
  intro:
    'We produce printed parasol umbrellas that are perfect for providing cover from the sun, or if the weather turns. The wide branding area gives your logo plenty of room to make a big impact.',
  heroImages: [
    '/home/parasols/parasol-hero.jpg',
    '/home/parasols/wildjac-2.jpg',
    '/home/parasols/petal-bar-parasol.jpg',
    '/home/parasols/wildjac-parasol.jpg',
    '/home/parasols/nothing-bound-parasol-2.jpg',
    '/home/parasols/hodgehill-parasol.jpg',
    '/home/parasols/hodgehill-parasol-2.jpg',
    '/home/parasols/parasol-mockup.jpg',
    '/home/parasols/parasol-hero.jpg',
    '/home/parasols/petal-bar-parasol.jpg',
    '/home/parasols/wildjac-2.jpg',
    '/home/parasols/hodgehill-parasol.jpg',
    '/home/parasols/nothing-bound-parasol-2.jpg',
    '/home/parasols/wildjac-parasol.jpg',
    '/home/parasols/hodgehill-parasol-2.jpg',
    '/home/parasols/parasol-mockup.jpg',
    '/home/parasols/parasol-hero.jpg',
    '/home/parasols/wildjac-parasol.jpg',
    '/home/parasols/petal-bar-parasol.jpg',
    '/home/parasols/hodgehill-parasol.jpg',
    '/home/parasols/nothing-bound-parasol-2.jpg',
  ],

  useCases: [
    { title: 'Pubs and cafes', text: 'Branded outdoor seating areas that attract attention and shade your customers.' },
    { title: 'Restaurants', text: 'Terrace and beer garden parasols that carry your branding outside.' },
    { title: 'Food festivals', text: 'High-visibility parasols that draw customers in at markets and outdoor events.' },
    { title: 'Corporate events and weddings', text: 'Custom parasols that add branding or elegance to a venue for the day.' },
  ],

  optionsHeading: 'Choose your parasol',
  optionsIntro:
    "We take the guesswork out of selecting a parasol. We've chosen two brands that reflect our quality and suit your budget, or supply your own for customisation.",
  options: [
    {
      id: '2-1m',
      title: '2.1m parasols',
      lead: 'A round parasol, perfect for smaller spaces and single tables.',
      body: 'Our entry-level parasol, supplied with a crank handle for easy opening and closing. Replacement canopies are available if you want to switch up your branding later.',
      image: '/home/parasols/wildjac-2.jpg',
      specs: [
        { label: 'Size', value: '2.1m round canopy. From £80 + VAT.' },
        { label: 'Colours', value: 'Black, natural, plum or green, subject to availability.' },
        { label: 'Frame', value: 'Crank handle operation, aluminium mast, six aluminium ribs.' },
        { label: 'Base', value: 'Sold separately, 15kg recommended.' },
      ],
    },
    {
      id: '2-5m-round',
      title: '2.5m round parasols',
      lead: 'A larger round parasol for bigger tables and more coverage.',
      body: 'Push-up mechanism for quick opening, on a heavier anodised aluminium mast built to stand up to daily use outdoors.',
      image: '/home/parasols/wildjac-parasol.jpg',
      specs: [
        { label: 'Size', value: '2.5m round canopy. From £170 + VAT.' },
        { label: 'Colours', value: 'Black, natural, green, blue or burgundy, subject to availability.' },
        { label: 'Frame', value: 'Push-up mechanism, 38mm anodised aluminium mast, 19mm aluminium ribs.' },
        { label: 'Base', value: 'Sold separately, 30kg recommended.' },
      ],
    },
    {
      id: '2-5m-square',
      title: '2.5m square parasols',
      lead: 'A square canopy that suits a run of tables or a squared-off terrace.',
      body: 'Same frame and base as our 2.5m round, with a square canopy for a different look and more edge to brand.',
      image: '/home/parasols/petal-bar-parasol.jpg',
      specs: [
        { label: 'Size', value: '2.5m square canopy. From £180 + VAT.' },
        { label: 'Colours', value: 'Black, natural, green, blue or burgundy, subject to availability.' },
        { label: 'Frame', value: 'Push-up mechanism, 38mm anodised aluminium mast, 19mm aluminium ribs.' },
        { label: 'Base', value: 'Sold separately, 30kg recommended.' },
      ],
    },
    {
      id: 'replacement-canopies',
      title: 'Replacement canopies',
      lead: 'Swap your canopy without replacing the whole parasol.',
      body: 'Want to mix it up sometimes? We supply replacement canopies for our parasols, which are easy to switch out. Run different branding on multiple canopies depending on your event or promotion.',
      image: '/home/parasols/nothing-bound-parasol-2.jpg',
      specs: [
        { label: 'Fit', value: 'Sized to match our 2.1m and 2.5m frames.' },
        { label: 'Use', value: 'Swap between events, seasons or promotions on the same frame.' },
      ],
    },
  ],

  customisation: [
    {
      title: 'Direct to Film (DTF)',
      text: 'Great for multiple colours and small details. Handles very small text, fine lines, gradients, photos, grunge effects, fluorescent, metallic and glitter or holographic finishes. No set-up fee, no minimum order.',
    },
    {
      title: 'Heat Transfer Vinyl (HTV)',
      text: 'Best suited to simple designs, logos and lettering. Artwork is cut rather than printed, so edges are sharp and crisp. Handles fluorescent, metallic and glitter or holographic finishes. No set-up fee, no minimum order.',
    },
  ],

  gallery: [
    '/home/parasols/parasol-hero.jpg',
    '/home/parasols/wildjac-2.jpg',
    '/home/parasols/wildjac-parasol.jpg',
    '/home/parasols/petal-bar-parasol.jpg',
    '/home/parasols/nothing-bound-parasol-2.jpg',
    '/home/parasols/hodgehill-parasol.jpg',
    '/home/parasols/hodgehill-parasol-2.jpg',
  ],

  steps: [
    {
      title: 'Choose your canvas',
      text: 'Drop off your own parasol to us, or select one of our tried and tested parasols.',
    },
    {
      title: 'Get a free quote and mockup',
      text: 'Send us your logo and our design team will put together a visual of how your parasol could look.',
    },
    {
      title: 'Approve your order',
      text: "After you approve your order, we'll get it into production, which usually takes 5 to 10 working days.",
    },
    {
      title: 'Order up',
      text: "We'll let you know when your order is ready to collect from our shop in Kidderminster, or we can arrange delivery to you.",
    },
  ],

  info: [
    {
      title: 'Customisation pricing',
      text: 'Applied per panel up to 500 × 400mm. Discounts apply for multiple parasols.',
      bullets: [
        '1 panel – £50 + VAT',
        '2 panels – £60 + VAT (£30 each)',
        '3 panels – £75 + VAT (£25 each)',
        '4 panels – £80 + VAT (£20 each)',
        '6 panels – £90 + VAT (£15 each)',
      ],
    },
    {
      title: 'Turnaround',
      text: 'Production typically takes 5 to 10 working days from approval. Need it sooner? Talk to us and we will do our best.',
    },
    {
      title: 'Collection and delivery',
      text: 'Collect from our shop in Kidderminster, or we can arrange delivery to you.',
    },
  ],

  cta: {
    title: 'Get your free parasol quote and mockup.',
    text: "Tell us about your outdoor space or event and we'll recommend the right parasol, size and customisation.",
    image: '/home/parasols/hodgehill-parasol.jpg',
  },
};

export default parasols;
